import { X, Info, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { useAnnouncements, Announcement } from '@/hooks/useAnnouncements';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const typeStyles = {
  info: {
    bg: 'bg-primary/10 border-primary/30',
    text: 'text-primary',
    icon: Info,
  },
  warning: {
    bg: 'bg-warning/10 border-warning/30',
    text: 'text-warning',
    icon: AlertTriangle,
  },
  success: {
    bg: 'bg-success/10 border-success/30',
    text: 'text-success',
    icon: CheckCircle,
  },
  error: {
    bg: 'bg-destructive/10 border-destructive/30',
    text: 'text-destructive',
    icon: XCircle,
  },
};

interface AnnouncementBannerItemProps {
  announcement: Announcement;
  onDismiss: (id: string) => void;
}

const AnnouncementBannerItem = ({ announcement, onDismiss }: AnnouncementBannerItemProps) => {
  const style = typeStyles[announcement.type] || typeStyles.info;
  const IconComponent = style.icon;

  return (
    <div className={cn('border rounded-lg p-3 flex items-start gap-3', style.bg)}>
      <IconComponent className={cn('h-5 w-5 flex-shrink-0 mt-0.5', style.text)} />
      <div className="flex-1 min-w-0">
        <p className={cn('font-medium text-sm', style.text)}>{announcement.title}</p>
        <p className="text-sm text-muted-foreground mt-0.5">{announcement.content}</p>
      </div>
      {announcement.is_dismissible && (
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 flex-shrink-0 text-muted-foreground hover:text-foreground"
          onClick={() => onDismiss(announcement.id)}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};

export const AnnouncementBanner = () => {
  const { announcements, dismissAnnouncement } = useAnnouncements();

  if (announcements.length === 0) return null;

  return (
    <div className="space-y-2 mb-4">
      {announcements.map((announcement) => (
        <AnnouncementBannerItem
          key={announcement.id}
          announcement={announcement}
          onDismiss={dismissAnnouncement}
        />
      ))}
    </div>
  );
};
